
import { UseFormRegister, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { generateUrlSlug, ProductFormData } from "@/utils/productHelpers";

interface ProductSEOManagerProps {
  register: UseFormRegister<ProductFormData>;
  watch: UseFormWatch<ProductFormData>;
  setValue: UseFormSetValue<ProductFormData>;
}

const ProductSEOManager = ({ register, watch, setValue }: ProductSEOManagerProps) => {
  const seoEnabled = watch("seo_enabled");
  const productName = watch("name");

  const handleGenerateSlug = () => {
    if (productName) {
      const slug = generateUrlSlug(productName);
      setValue("url_slug", slug);
    }
  };

  return (
    <Card className="admin-card border-admin-border">
      <CardHeader>
        <CardTitle className="text-admin-foreground">SEO Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="seo_enabled"
            checked={seoEnabled}
            onCheckedChange={(checked) => setValue("seo_enabled", checked)}
          />
          <Label htmlFor="seo_enabled">Enable SEO optimization</Label>
        </div>

        {seoEnabled && (
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="seo_title">SEO Title</Label>
              <Input
                id="seo_title"
                {...register("seo_title")}
                className="mt-1"
                placeholder="Enter SEO title"
              />
              <p className="text-sm text-admin-muted-foreground mt-1">
                Recommended: 50-60 characters
              </p>
            </div>

            <div>
              <Label htmlFor="seo_description">SEO Description</Label>
              <Textarea
                id="seo_description"
                {...register("seo_description")}
                className="mt-1"
                rows={3}
                placeholder="Enter SEO description"
              />
              <p className="text-sm text-admin-muted-foreground mt-1">
                Recommended: 150-160 characters
              </p>
            </div>

            <div>
              <Label htmlFor="seo_keywords">SEO Keywords</Label>
              <Input
                id="seo_keywords"
                {...register("seo_keywords")}
                className="mt-1"
                placeholder="keyword1, keyword2, keyword3"
              />
              <p className="text-sm text-admin-muted-foreground mt-1">
                Separate keywords with commas
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2">
                <Label htmlFor="url_slug">URL Slug</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateSlug}
                  disabled={!productName}
                >
                  Generate from name
                </Button>
              </div>
              <Input
                id="url_slug"
                {...register("url_slug")}
                className="mt-1"
                placeholder="product-url-slug"
              />
              <p className="text-sm text-admin-muted-foreground mt-1">
                Used in product URLs. Only lowercase letters, numbers, and hyphens allowed.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductSEOManager;
